import { Field as ChakraField, Fieldset, HStack, Input, VStack } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import { InputNumber } from '@components/ui/input/InputNumber';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { areSelectedFieldsEqual } from '@utils/common.utils';
import { DistanceUnits, TimeUnits, unitLabel, WeightUnits } from '@utils/unit.utils';
import { hasChanged, isInvalid } from '@utils/validation/validation.utils';
import { Field, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';

import { PartialDrone } from '@/types/utm/drone.types';

export default function DronePublicTab() {
  const { errors, touched, initialValues, values, setFieldValue } = useFormikContext<PartialDrone>();
  const hasCollaborativeIdsChanged = !areSelectedFieldsEqual(values, initialValues, [
    'adsbNumber',
    'aeroscopeNumber',
    'skyEyeAntennaNumber',
    'groundStationNumber',
    'holodroneNumber',
  ]);

  return (
    <CockpitFormLayout title="components.utm.drones.tab.public.title">
      <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
        <VStack width="100%" gap={5} alignItems="start">
          <ChakraField.Root variant="cockpit">
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.utm.drones.collaborativeIds" />
              {hasCollaborativeIdsChanged && <ModifiedTag />}
            </ChakraField.Label>
            <HStack width="100%" gap={2}>
              <Field as={Input} type="text" name="adsbNumber" id="adsbNumber" />
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.utm.drones.tab.public.adsbNumber" />
              </ChakraField.Label>
            </HStack>
            <HStack width="100%" gap={2}>
              <Field as={Input} type="text" name="aeroscopeNumber" id="aeroscopeNumber" />
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.utm.drones.tab.public.aeroscopeNumber" />
              </ChakraField.Label>
            </HStack>
            <HStack width="100%" gap={2}>
              <Field as={Input} type="text" name="skyEyeAntennaNumber" id="skyEyeAntennaNumber" />
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.utm.drones.tab.public.skyEyeAntennaNumber" />
              </ChakraField.Label>
            </HStack>
            <HStack width="100%" gap={2}>
              <Field as={Input} type="text" name="groundStationNumber" id="groundStationNumber" />
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.utm.drones.tab.public.groundStationNumber" />
              </ChakraField.Label>
            </HStack>
            <HStack width="100%" gap={2}>
              <Field as={Input} type="text" name="holodroneNumber" id="holodroneNumber" />
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.utm.drones.tab.public.holodroneNumber" />
              </ChakraField.Label>
            </HStack>
          </ChakraField.Root>
          <ChakraField.Root variant="cockpit" required invalid={isInvalid('serialNumber', touched, errors)}>
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.utm.drones.tab.public.serialNumber" />
              <RequiredIndicator />
              {hasChanged('serialNumber', initialValues, values) && <ModifiedTag />}
            </ChakraField.Label>
            <Field as={Input} type="text" name="serialNumber" id="serialNumber" />
          </ChakraField.Root>
          <HStack width="100%" gap={2}>
            <ChakraField.Root variant="cockpit" invalid={isInvalid('model', touched, errors)}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.utm.drones.model" />
                {hasChanged('model', initialValues, values) && <ModifiedTag />}
              </ChakraField.Label>
              <Field as={Input} type="text" name="model" id="model" />
            </ChakraField.Root>
            <ChakraField.Root variant="cockpit" invalid={isInvalid('manufacturer', touched, errors)}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.utm.drones.manufacturer" />
                {hasChanged('manufacturer', initialValues, values) && <ModifiedTag />}
              </ChakraField.Label>
              <Field as={Input} type="text" name="manufacturer" id="manufacturer" />
            </ChakraField.Root>
          </HStack>
          <HStack width="100%" gap={2}>
            <ChakraField.Root variant="cockpit" invalid={isInvalid('strategicDrone.wight', touched, errors)}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.utm.drones.tab.public.weight" />
                {hasChanged('strategicDrone.weight', initialValues, values) && <ModifiedTag />}
              </ChakraField.Label>
              <Field
                as={InputNumber}
                name="strategicDrone.weight"
                id="strategicDrone.weight"
                min={0}
                suffix={unitLabel[WeightUnits.KILOGRAMS]}
                onChange={(value?: number) => setFieldValue('strategicDrone.weight', value ?? null)}
              />
            </ChakraField.Root>
            <ChakraField.Root variant="cockpit" invalid={isInvalid('strategicDrone.range', touched, errors)}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="components.utm.drones.tab.public.range" />
                {hasChanged('strategicDrone.range', initialValues, values) && <ModifiedTag />}
              </ChakraField.Label>
              <Field
                as={InputNumber}
                name="strategicDrone.range"
                id="strategicDrone.range"
                min={0}
                suffix={unitLabel[DistanceUnits.KILOMETERS]}
                onChange={(value?: number) => setFieldValue('strategicDrone.range', value ?? null)}
              />
            </ChakraField.Root>
          </HStack>
          <ChakraField.Root
            variant="cockpit"
            width="219px"
            invalid={isInvalid('strategicDrone.autonomy', touched, errors)}
          >
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.utm.drones.tab.public.autonomy" />
              {hasChanged('strategicDrone.autonomy', initialValues, values) && <ModifiedTag />}
            </ChakraField.Label>
            <Field
              as={InputNumber}
              name="strategicDrone.autonomy"
              id="strategicDrone.autonomy"
              min={0}
              suffix={unitLabel[TimeUnits.MINUTES]}
              onChange={(value?: number) => setFieldValue('strategicDrone.autonomy', value ?? null)}
            />
          </ChakraField.Root>
        </VStack>
      </Fieldset.Root>
    </CockpitFormLayout>
  );
}
